import { ECustomerSource } from 'core/model/enums/customer.enum';
import { IBusiness } from 'core/model/interfaces/business.interface';
import { IUser } from 'core/model/interfaces/customer.interface';
import type { IAddressCodeDTO, IAddressDTO, ICustomerDTO, IGetUserProfileResponseV2 } from './customers.dto';

const getBussinessId = (ids: IAddressCodeDTO): string => {
  if (ids.alicorp && ids.allied) return `${ids.alicorp}|${ids.allied}`;
  if (ids.alicorp) return ids.alicorp;
  if (ids.allied) return ids.allied;
  return '';
};

export const addressesDTOtoAddressesData = (addresses: Array<IAddressDTO>): Array<IBusiness> =>
  addresses
    .map((address, index) => ({
      department: address.department,
      district: address.district,
      province: address.province,
      reference: '',
      ubigeoCode: address.localCode,
      isMain: index === 0,
      address: address.address,
      id: getBussinessId(address.ids),
      ids: address.ids,
      customerId: address.customerId,
    }))
    .sort(a => (a.isMain ? -1 : 1));

const getDefaultCustomerValues = (
  alicorpInfo: ICustomerDTO | undefined,
  alliedInfo: ICustomerDTO | undefined,
  sourceId: number,
) => {
  let distributorName = '';
  const priceGroup = {
    id: '',
    description: '',
  };
  const priceList = {
    id: '',
    description: '',
  };
  let defaultDistributor;

  if (sourceId === ECustomerSource.DEX || sourceId === ECustomerSource.HYBRID) {
    defaultDistributor = alicorpInfo;
  } else if (sourceId === ECustomerSource.APUDEX) {
    defaultDistributor = alliedInfo;
  }

  if (defaultDistributor) {
    distributorName = defaultDistributor?.distributor?.name ?? '';
    priceGroup.id = defaultDistributor?.priceGroup?.id.toString() ?? '';
    priceGroup.description = defaultDistributor?.priceGroup?.name ?? '';
    priceList.id = defaultDistributor?.priceList?.id.toString() ?? '';
    priceList.description = defaultDistributor?.priceList?.name ?? '';
  }

  return {
    distributorName,
    priceGroup,
    priceList,
  };
};

const getDistributors = (customers: Array<ICustomerDTO>) => {
  let alicorpInfo: ICustomerDTO | undefined;
  let alliedInfo: ICustomerDTO | undefined;

  customers.some(customer => {
    if (customer.source.id === ECustomerSource.DEX && !alicorpInfo) {
      alicorpInfo = customer;
    } else if (customer.source.id === ECustomerSource.APUDEX && !alliedInfo) {
      alliedInfo = customer;
    }
    return !!(alicorpInfo && alliedInfo);
  });

  return {
    alicorpInfo,
    alliedInfo,
  };
};

export const toCustomersDomain = (profileData: IGetUserProfileResponseV2): IUser => {
  if (!profileData.customers) throw new Error('This user has no customers');
  const { alicorpInfo, alliedInfo } = getDistributors(profileData.customers);

  const { distributorName, priceGroup, priceList } = getDefaultCustomerValues(
    alicorpInfo,
    alliedInfo,
    profileData.source.id,
  );

  const reponse: IUser = {
    id: profileData.id.toString(),
    basicData: {
      document: { type: profileData.document.type, number: profileData.document.number },
      email: profileData.email ?? '',
      businessName: profileData.name,
      firstLastName: profileData.lastName ?? '',
      name: profileData.name,
      secondLastName: profileData.motherLastName ?? '',
      phones: [{ number: profileData.phone ?? '', isMain: true }],
    },
    priceGroup,
    priceList,
    distributorName,
    source: profileData.source.id,
    legalConsent: profileData.isLegalConsentAccepted,
    dataPrivacyConsent: profileData.isPrivacyConsentAccepted,
    channel: {
      id: profileData?.channel?.id ?? 1,
      description: profileData?.channel?.name ?? 'Insuma',
    },
    customers: profileData.customers.map(customer => ({
      id: customer.id,
      source: customer.source.id,
    })),
    isWhatsappButtonEnabled: false,
  };

  return reponse;
};
